/**
 * FilterChips - Shared component for filter chip rows
 * 
 * Sprint I Block I-1: Replaces maintenance alert banners with filter chips.
 * Each chip displays a label + count and acts as a clickable filter.
 * 
 * Features:
 * - Active chip is visually selected
 * - Supports semantic colors (neutral, warning, critical)
 * - Light + dark theme support
 * - Fully i18n driven (no hardcoded strings)
 * 
 * @created Sprint I Block I-1
 */

import { cn } from '../../lib/utils';

/**
 * @typedef {Object} FilterChipItem
 * @property {string} id - Unique identifier for the filter
 * @property {string} label - Display label (i18n translated)
 * @property {number} count - Count to display
 * @property {'neutral' | 'warning' | 'critical'} [variant] - Color variant
 */

/**
 * FilterChips component
 * 
 * @param {Object} props
 * @param {FilterChipItem[]} props.items - Array of chip items
 * @param {string} props.activeId - Currently active filter ID
 * @param {(id: string) => void} props.onSelect - Callback when a chip is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
export const FilterChips = ({ items, activeId, onSelect, className }) => {
  return (
    <div 
      className={cn("flex flex-wrap items-center gap-2", className)}
      data-testid="filter-chips"
      role="group"
      aria-label="Filter options"
    >
      {items.map((item) => {
        const isActive = activeId === item.id;
        const variant = item.variant || 'neutral';
        
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            data-testid={`filter-chip-${item.id}`}
            aria-pressed={isActive}
            className={cn(
              // Base styles
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium",
              "transition-all duration-150 ease-in-out",
              "border focus:outline-none focus:ring-2 focus:ring-offset-1",
              
              // Variant-specific styles
              variant === 'critical' && !isActive && [
                "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
                "dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/30"
              ],
              variant === 'critical' && isActive && [
                "bg-red-600 text-white border-red-600 shadow-sm",
                "dark:bg-red-600 dark:text-white dark:border-red-500"
              ],
              
              variant === 'warning' && !isActive && [
                "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100",
                "dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/30"
              ],
              variant === 'warning' && isActive && [
                "bg-amber-500 text-white border-amber-500 shadow-sm",
                "dark:bg-amber-500 dark:text-white dark:border-amber-400"
              ],
              
              variant === 'neutral' && !isActive && [
                "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200",
                "dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700"
              ],
              variant === 'neutral' && isActive && [
                "bg-slate-700 text-white border-slate-700 shadow-sm",
                "dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
              ],
              
              // Focus ring color based on variant
              variant === 'critical' && "focus:ring-red-500",
              variant === 'warning' && "focus:ring-amber-500",
              variant === 'neutral' && "focus:ring-slate-500"
            )}
          >
            <span>{item.label}</span>
            <span 
              className={cn(
                "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold",
                // Count badge styling
                variant === 'critical' && !isActive && "bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200",
                variant === 'critical' && isActive && "bg-red-500 text-white dark:bg-red-500 dark:text-white",
                variant === 'warning' && !isActive && "bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200",
                variant === 'warning' && isActive && "bg-amber-400 text-white dark:bg-amber-400 dark:text-white",
                variant === 'neutral' && !isActive && "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
                variant === 'neutral' && isActive && "bg-slate-600 text-white dark:bg-slate-400 dark:text-slate-900"
              )}
            >
              {item.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default FilterChips;
